import type { Dispatch } from 'react';
import { useState, useMemo, useEffect } from 'react';
import { graphql } from 'react-relay';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import {
	useMutationInterop,
	useQueryInterop,
	type MutationFunctionInterop as MutationFunction,
	type ApolloErrorInterop,
} from '@confluence/apollo-relay-interop';
import { fg } from '@confluence/feature-gating';

import type { ClassificationLevel } from '../ClassificationRadioOption.tsx';
import { NO_CLASSIFICATION_OPTION_ID } from '../constants/NoClassification.ts';
import { FlagStatus } from '../constants/FlagStatus.ts';
import { Status } from '../constants/Status.ts';
import type { ErrorState } from '../constants/ErrorState.ts';
import { getQuerySingleContentStatusArrayVariable } from '../utils/getQuerySingleContentStatusArrayVariable.ts';
import { getReadAndWriteContextFromQueryContext } from '../utils/getReadAndWriteContextFromQueryContext.ts';

import type {
	useContentClassificationContentDataClassificationQuery$data,
	useContentClassificationContentDataClassificationQuery$variables,
} from './__generated__/useContentClassificationContentDataClassificationQuery.graphql.ts';
import type {
	useContentClassificationBulkUpdateContentDataClassificationLevelMutation$data,
	useContentClassificationBulkUpdateContentDataClassificationLevelMutation$variables,
} from './__generated__/useContentClassificationBulkUpdateContentDataClassificationLevelMutation.graphql.ts';
import type {
	useContentClassificationBulkDeleteContentDataClassificationLevelMutation$data,
	useContentClassificationBulkDeleteContentDataClassificationLevelMutation$variables,
} from './__generated__/useContentClassificationBulkDeleteContentDataClassificationLevelMutation.graphql.ts';
import { useClassificationErrorState } from './useClassificationErrorState.ts';

const UseContentClassificationContentDataClassificationQuery = graphql`
	query useContentClassificationContentDataClassificationQuery(
		$contentId: ID!
		$lowerCaseContentStatusArray: [String]
	) {
		singleContent(id: $contentId, status: $lowerCaseContentStatusArray) {
			id
			classificationLevelDetails {
				classificationLevel {
					id
					guideline
					description
					color
					name
					status
				}
				featureEnabled
				source
				isReclassificationPermitted
			}
		}
	}
`;

/**
 * Return type for useContentClassification hook. Includes:
 * 1) Content's classification level by contentId & by spaceId,
 * 2) Mutation functions for updating & deleting the content's classification,
 * 3) Flag status for rendering error flag upon updating classification.
 */
export type ContentClassificationMetadata = {
	classification: ClassificationLevel | null;
	spaceDefaultIsOverridden: boolean;
	errorState: ErrorState;
	contentLevelError?: ApolloErrorInterop;
	shouldDisplayClassification: boolean;
	updateContentDataClassificationLevel: MutationFunction<
		useContentClassificationBulkUpdateContentDataClassificationLevelMutation$data,
		useContentClassificationBulkUpdateContentDataClassificationLevelMutation$variables
	>;
	deleteContentDataClassificationLevel: MutationFunction<
		useContentClassificationBulkDeleteContentDataClassificationLevelMutation$data,
		useContentClassificationBulkDeleteContentDataClassificationLevelMutation$variables
	>;
	setSubmittedClassification: Dispatch<ClassificationLevel | null>;
	flagStatus: FlagStatus;
	setFlagStatus: Dispatch<FlagStatus>;
	hasNoClassification?: boolean;
	contentLevelLoading?: boolean;
	contentStatusContext: ClassificationQueryContext;
	featureEnabled?: boolean;
	isReclassificationPermitted?: boolean;
};

// Note: this behavior is extracted into its own hook to allow for easier testing
interface UseSyncSubmittedClassificationOnFlagChangeI {
	flagStatus: FlagStatus;
	submittedClassification?: ClassificationLevel | null;
	setClassification: Dispatch<ClassificationLevel | null>;
	setFlagStatus: Dispatch<FlagStatus>;
}

export const useSyncSubmittedClassificationOnFlagChange = ({
	flagStatus,
	submittedClassification,
	setClassification,
	setFlagStatus,
}: UseSyncSubmittedClassificationOnFlagChangeI) => {
	// Updates the classification state with the value of submittedClassification
	// on flag success, ensuring consistent behavior across edit tag & edit modal
	useEffect(() => {
		if (flagStatus === FlagStatus.SUCCESS) {
			setClassification(submittedClassification ?? null);
			setFlagStatus(FlagStatus.NONE);
		}
	}, [flagStatus, submittedClassification, setClassification, setFlagStatus]);
};

/**
 * Determines the context of the ContentStatus for which the content classification is read and written.
 * 'draft-only' reads and writes to 'draft',
 * 'current-only' reads and writes to 'current',
 * 'current-and-draft' reads from 'current' and writes to 'current' & 'draft' (live-pages)
 */
export type ClassificationQueryContext = 'draft-only' | 'current-only' | 'current-and-draft';

type useContentClassificationType = {
	contentId?: string;
	spaceKeyOverride?: string | null;
	shouldSkipQueries?: boolean;
	contentStatusContext?: ClassificationQueryContext;
	shouldPreloadData?: boolean;
	shouldReadFromQueries?: boolean;
};

/**
 *
 * @param contentId
 * @param spaceKeyOverride
 * @param contentStatusContext
 * @param shouldSkipQueries
 * @returns
 * 1) Content's classification level by contentId & by spaceId,
 * 2) Mutation functions for updating & deleting the content's classification,
 * 3) Flag status for rendering error flag upon updating classification.
 */
export const useContentClassification = ({
	contentId,
	contentStatusContext = 'current-only',
	shouldSkipQueries = false,
	shouldPreloadData = false,
	shouldReadFromQueries = false,
}: useContentClassificationType): ContentClassificationMetadata => {
	const shouldPreloadContentClassificationData = window.__SSR_RENDERED__ || shouldPreloadData;

	const [flagStatus, setFlagStatus] = useState(FlagStatus.NONE);
	const [classification, setClassification] = useState<ClassificationLevel | null>(null);
	const [currentContentId, setCurrentContentId] = useState<string | undefined>(undefined);
	const [submittedClassification, setSubmittedClassification] =
		useState<ClassificationLevel | null>();

	const renderServerPlaceholder = useRenderServerPlaceholder();

	const { readContext: contentStatusReadContext } =
		getReadAndWriteContextFromQueryContext(contentStatusContext);

	useSyncSubmittedClassificationOnFlagChange({
		flagStatus,
		submittedClassification,
		setClassification,
		setFlagStatus,
	});

	// The following two mutation functions are returned by this hook, behavior upon completion is handled within the hook
	const [updateContentDataClassificationLevel] = useMutationInterop<
		useContentClassificationBulkUpdateContentDataClassificationLevelMutation$data,
		useContentClassificationBulkUpdateContentDataClassificationLevelMutation$variables
	>(
		graphql`
			mutation useContentClassificationBulkUpdateContentDataClassificationLevelMutation(
				$id: Long!
				$contentStatuses: [ContentDataClassificationMutationContentStatus]!
				$classificationLevelId: ID!
			) {
				bulkUpdateContentDataClassificationLevel(
					input: {
						id: $id
						contentStatuses: $contentStatuses
						classificationLevelId: $classificationLevelId
					}
				) {
					success
					errors {
						message
					}
				}
			}
		`,
		{
			awaitRefetchQueries: true,
			onError: (updateClassificationError) => {
				setFlagStatus(FlagStatus.ERROR);
				markErrorAsHandled(updateClassificationError);
			},
			onCompleted: (data) => {
				if (data?.bulkUpdateContentDataClassificationLevel?.success) {
					setFlagStatus(FlagStatus.SUCCESS);
				}
			},
			refetchQueries: () => [
				{
					query: UseContentClassificationContentDataClassificationQuery,
					variables: {
						contentId,
						lowerCaseContentStatusArray:
							getQuerySingleContentStatusArrayVariable(contentStatusReadContext),
					},
				},
			],
		},
	);

	const [deleteContentDataClassificationLevel, { loading: deleteLoading }] = useMutationInterop<
		useContentClassificationBulkDeleteContentDataClassificationLevelMutation$data,
		useContentClassificationBulkDeleteContentDataClassificationLevelMutation$variables
	>(
		graphql`
			mutation useContentClassificationBulkDeleteContentDataClassificationLevelMutation(
				$id: Long!
				$contentStatuses: [ContentDataClassificationMutationContentStatus]!
			) {
				bulkDeleteContentDataClassificationLevel(
					input: { id: $id, contentStatuses: $contentStatuses }
				) {
					success
					errors {
						message
					}
				}
			}
		`,
		{
			awaitRefetchQueries: true,
			onError: (deleteClassificationError) => {
				setFlagStatus(FlagStatus.ERROR);
				markErrorAsHandled(deleteClassificationError);
			},
			onCompleted: (data) => {
				if (data?.bulkDeleteContentDataClassificationLevel?.success) {
					setFlagStatus(FlagStatus.SUCCESS);
				}
			},
			refetchQueries: () => [
				{
					query: UseContentClassificationContentDataClassificationQuery,
					variables: {
						contentId,
						lowerCaseContentStatusArray:
							getQuerySingleContentStatusArrayVariable(contentStatusReadContext),
					},
				},
			],
		},
	);

	const shouldSkipQueriesForClassification = shouldSkipQueries || !contentId;

	const {
		data: objectLevelData,
		loading: objectLevelLoading,
		error: objectLevelError,
	} = useQueryInterop<
		useContentClassificationContentDataClassificationQuery$data,
		useContentClassificationContentDataClassificationQuery$variables
	>(UseContentClassificationContentDataClassificationQuery, {
		variables: {
			contentId: contentId ?? '',
			lowerCaseContentStatusArray:
				getQuerySingleContentStatusArrayVariable(contentStatusReadContext),
		},
		skip: shouldSkipQueriesForClassification,
		errorPolicy: 'all',
		fetchPolicy: shouldPreloadContentClassificationData ? 'cache-first' : 'network-only',
	});

	const classificationLevelDetails = objectLevelData?.singleContent?.classificationLevelDetails;
	const contentClassification = classificationLevelDetails?.classificationLevel;

	const contentLevelLoading = objectLevelLoading;
	const contentLevelError = objectLevelError;

	const spaceDefaultIsOverridden = classificationLevelDetails?.source === 'CONTENT';

	const errorState = useClassificationErrorState(classification, undefined, contentLevelError);

	// Determine whether to set classification either content level classification, or space default classification, or null through a series of booleans.

	// Cases where we should not display content classification:
	// 1. If "No classification" option is selected as content classification
	// 2. If content classification was archived
	// 3. If classification value from state hook is null (i.e. classification was deleted) and delete mutation is still loading
	// 4. If delete was successful and classification value from state hook is null
	// 5. If there was an error fetching content level classification
	const shouldNotDisplayContentClassification =
		contentClassification?.id === NO_CLASSIFICATION_OPTION_ID ||
		contentClassification?.status === Status.ARCHIVED ||
		(classification === null && deleteLoading) ||
		Boolean(contentLevelError);

	// This ternary determines whether to display content classification value from query or from state hook.
	// Checks whether content ID has changed since the last render, and if it has then we display the classification value from query.
	// (Resolves bug where classification wasn't properly updating when navigating between pages via page tree)
	const contentClassificationFromQueryOrState = shouldReadFromQueries
		? contentClassification
		: currentContentId === contentId && !!classification
			? classification
			: contentClassification;

	// Ultimately, we'll be displaying one of the two values as a content's current classification (in order of priority):
	// 1. Content level classification
	// 2. No classification (may render Add Classification button instead)
	const updatedClassification = shouldNotDisplayContentClassification
		? null
		: contentClassificationFromQueryOrState;

	useEffect(() => {
		if (updatedClassification !== classification) {
			setClassification((updatedClassification as ClassificationLevel) ?? null);
			setCurrentContentId(contentId);
		}
	}, [updatedClassification, classification, contentId]);

	const isClassificationPublished =
		classification?.status === Status.PUBLISHED ||
		updatedClassification?.status === Status.PUBLISHED;

	const classificationFeatureEnabled = Boolean(
		objectLevelData?.singleContent?.classificationLevelDetails?.featureEnabled,
	);

	const shouldDisplayClassification =
		classificationFeatureEnabled &&
		(!!classification || !!updatedClassification) &&
		!contentLevelLoading &&
		isClassificationPublished;

	const hasNoClassification = !contentLevelLoading && !classification && !updatedClassification;

	const isReclassificationPermitted =
		(objectLevelData?.singleContent?.classificationLevelDetails?.isReclassificationPermitted ??
			false) ||
		!fg('dlp_classification_access');

	//useEffect is not run during SSR, so we can't use classification from the state.
	//we cant use setClassification either as it causes side effects which causes unnecessary re-renders.
	//Instead, we can use updatedClassification for initial load for SSR.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSRedWithClassification = renderServerPlaceholder && updatedClassification;

	const classificationValue = isSSRedWithClassification
		? (updatedClassification ?? null)
		: (classification ?? updatedClassification ?? null);

	return useMemo<ContentClassificationMetadata>(
		() => ({
			classification: (classificationValue as ClassificationLevel) ?? null,
			spaceDefaultIsOverridden,
			contentLevelLoading,
			contentLevelError,
			updateContentDataClassificationLevel,
			deleteContentDataClassificationLevel,
			setSubmittedClassification,
			flagStatus,
			setFlagStatus,
			errorState,
			shouldDisplayClassification,
			hasNoClassification,
			contentStatusContext,
			featureEnabled: classificationFeatureEnabled,
			isReclassificationPermitted,
		}),
		[
			classificationValue,
			spaceDefaultIsOverridden,
			contentLevelLoading,
			contentLevelError,
			updateContentDataClassificationLevel,
			deleteContentDataClassificationLevel,
			setSubmittedClassification,
			flagStatus,
			setFlagStatus,
			errorState,
			shouldDisplayClassification,
			hasNoClassification,
			contentStatusContext,
			classificationFeatureEnabled,
			isReclassificationPermitted,
		],
	);
};
